import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle, Loader2, Send, Mail } from 'lucide-react';
import './RotatorForm.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = '0x4AAAAAAD8DqRvauFgRbwdq';
const CARIBBEAN_IMG = 'https://static.prod-images.emergentagent.com/jobs/c48739c8-01fe-4eac-8344-d77f44a771b1/images/30135d72b5827c40b529e96e64aa50b7799de745872f85ec8d3a1e0285666788.jpeg';
const COINS_IMG = 'https://static.prod-images.emergentagent.com/jobs/c48739c8-01fe-4eac-8344-d77f44a771b1/images/a8d4bad8978b1d686d338048ebd774931168b689ef56f4188408e974db6d786c.jpeg';

const INTEREST_OPTIONS = [
  {
    key: 'freiheit',
    icon: '🌍',
    title: 'Ortsunabhängig arbeiten & selbstbestimmt leben',
    desc: 'Arbeite von wo du willst und mach dich frei von Ort, Alltag und System.',
  },
  {
    key: 'krypto',
    icon: '₿',
    title: 'Kapital für dich arbeiten lassen & Krypto meistern',
    desc: 'Werde deine eigene Bank, lerne sichere & währungsunabhängige Anlagemöglichkeiten kennen und bezahle mit Krypto easy weltweit.',
  },
];

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

export default function RotatorForm() {
  const [email, setEmail] = useState('');
  const [interests, setInterests] = useState([]);
  const [message, setMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [turnstileRendered, setTurnstileRendered] = useState(false);
  const turnstileRef = useRef(null);
  const widgetId = useRef(null);

  // "Loslegen" bei einem Projekt auf der Startseite -> Projektname ins Nachrichtenfeld uebernehmen
  useEffect(() => {
    const onPrefill = (e) => {
      const name = e.detail?.project;
      if (!name) return;
      const line = `Ich interessiere mich für das Projekt „${name}“.`;
      setMessage(prev => (prev.includes(line) ? prev : (prev.trim() ? `${prev.trim()}\n${line}` : line)));
    };
    window.addEventListener('rotator-prefill', onPrefill);
    return () => window.removeEventListener('rotator-prefill', onPrefill);
  }, []);

  useEffect(() => {
    const scriptId = 'cf-turnstile-script';
    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current && widgetId.current === null) {
        try {
          widgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'light',
            callback: (token) => { setTurnstileToken(token); setTurnstileError(''); },
            'error-callback': () => {
              setTurnstileToken('');
              setTurnstileError('Bot-Schutz konnte nicht geladen werden. Bitte lade die Seite neu.');
            },
            'expired-callback': () => setTurnstileToken(''),
            'timeout-callback': () => setTurnstileToken(''),
          });
          setTurnstileRendered(true);
        } catch (e) { /* already rendered */ }
      }
    };
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else if (window.turnstile) {
      renderWidget();
    } else {
      const t = setInterval(() => { if (window.turnstile) { renderWidget(); clearInterval(t); } }, 300);
      setTimeout(() => clearInterval(t), 8000);
    }
    return () => {
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch (e) { /* noop */ }
        widgetId.current = null;
      }
    };
  }, []);

  const toggleInterest = (key) => {
    setInterests(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const emailValid = EMAIL_RE.test(email.trim());
  const contentValid = interests.length > 0 || message.trim().length >= 10;

  const resetTurnstile = () => {
    setTurnstileToken('');
    if (window.turnstile && widgetId.current !== null) {
      try { window.turnstile.reset(widgetId.current); } catch (e) { /* noop */ }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!emailValid) { setError('Bitte gib eine gültige E-Mail-Adresse ein.'); return; }
    if (!contentValid) { setError('Bitte wähle mindestens eine Option aus oder beschreibe dein Anliegen mit mindestens 10 Zeichen.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/community/rotator/submit`, {
        email: email.trim(),
        interests,
        message: message.trim(),
        turnstile_token: turnstileToken,
      });
      setShowSuccess(true);
      setEmail(''); setInterests([]); setMessage('');
      resetTurnstile();
    } catch (err) {
      setError(err.response?.data?.detail || 'Etwas ist schiefgelaufen. Bitte versuche es erneut.');
      resetTurnstile();
    }
    setSubmitting(false);
  };

  return (
    <div className="rotator-wrap" data-testid="rotator-form-wrap">
      <div className="rotator-visual">
        <img src={CARIBBEAN_IMG} alt="Ortsunabhängig arbeiten in der Karibik" className="rotator-photo" draggable="false" />
        <img src={COINS_IMG} alt="Krypto Coins: Bitcoin, Ethereum, Solana, Shiba Inu" className="rotator-coins" draggable="false" />
        <p className="rotator-visual-caption">Dein Leben. Deine Regeln. Deine Freiheit.</p>
      </div>

      <div className="rotator-form-col">
        <h3 className="rotator-heading">Finde deinen persönlichen Förderer</h3>
        <p className="rotator-sub">
          Sag uns kurz, was dich interessiert – wir verbinden dich mit einem unserer Botschafter,
          der sich persönlich bei dir meldet und dich Schritt für Schritt begleitet.
        </p>

        <form onSubmit={handleSubmit} className="rotator-form" data-testid="rotator-form">
          <label className="rotator-label" htmlFor="rotator-email">Deine E-Mail-Adresse *</label>
          <div className="rotator-email-wrap">
            <Mail size={16} className="rotator-email-icon" />
            <input
              id="rotator-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="deine@email.de"
              className="rotator-email-input"
              maxLength={254}
              required
              data-testid="rotator-email-input"
            />
          </div>

          <label className="rotator-label">Was interessiert dich? (Mehrfachauswahl möglich)</label>
          <div className="rotator-options">
            {INTEREST_OPTIONS.map(opt => (
              <label key={opt.key} className={`rotator-option ${interests.includes(opt.key) ? 'selected' : ''}`} data-testid={`rotator-option-${opt.key}`}>
                <input
                  type="checkbox"
                  checked={interests.includes(opt.key)}
                  onChange={() => toggleInterest(opt.key)}
                  data-testid={`rotator-checkbox-${opt.key}`}
                />
                <span className="rotator-option-icon">{opt.icon}</span>
                <span className="rotator-option-text">
                  <strong>{opt.title}</strong>
                  <small>{opt.desc}</small>
                </span>
              </label>
            ))}
          </div>

          <label className="rotator-label" htmlFor="rotator-message">
            Erzähl uns mehr über dich und deine Ziele <span className="rotator-optional">(optional, falls du oben etwas ausgewählt hast)</span>
          </label>
          <textarea
            id="rotator-message"
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Was möchtest du erreichen? Wo stehst du gerade? Nenne gern ein konkretes Projekt, für das du dich interessierst."
            className="rotator-textarea"
            maxLength={4000}
            rows={5}
            data-testid="rotator-message-input"
          />
          <div className="rotator-charcount" data-testid="rotator-charcount">{message.length} / 4000 Zeichen</div>

          <div className="rotator-turnstile" ref={turnstileRef} data-testid="rotator-turnstile" />
          {turnstileError && (
            <div className="rotator-error" data-testid="rotator-turnstile-error">
              <AlertCircle size={15} /><span>{turnstileError}</span>
            </div>
          )}

          {error && (
            <div className="rotator-error" data-testid="rotator-error">
              <AlertCircle size={15} /><span>{error}</span>
            </div>
          )}

          <button type="submit" className="rotator-submit" disabled={submitting || !emailValid || !contentValid || (turnstileRendered && !turnstileError && !turnstileToken)} data-testid="rotator-submit">
            {submitting ? <Loader2 size={17} className="spin" /> : <Send size={17} />}
            Förderer finden
          </button>
        </form>
      </div>

      {showSuccess && (
        <div className="rotator-success-overlay" onClick={() => setShowSuccess(false)} data-testid="rotator-success-popup">
          <div className="rotator-success-card" onClick={e => e.stopPropagation()}>
            <CheckCircle size={52} className="rotator-success-icon" />
            <h3>Vielen Dank!</h3>
            <p>Dein persönlicher Förderer wurde soeben informiert und meldet sich in Kürze per E-Mail bei dir. Wir freuen uns auf dich! 🚀</p>
            <button className="rotator-success-close" onClick={() => setShowSuccess(false)} data-testid="rotator-success-close">Alles klar!</button>
          </div>
        </div>
      )}
    </div>
  );
}
